import { useEffect, useRef, useState, useCallback, FormEvent } from "react";
import { showToast } from "../utils/toast";
import { ChatInputForm } from "./ChatInputForm";
import { ModeId } from "../types/Mode";
import { CHAT_MODES } from "../../constant/chat";
import { useFileUpload } from "../hooks/useFileUpload";
import { useChatStream } from "../hooks/useChatStream";
import { useChatContext } from "./App";
import { UserMessage } from "./Chat/UserMessage";
import { AssistantMessage } from "./Chat/AssistantMessage";
import { ToolCall } from "./ToolCall";
import { useAutoScroll } from "../hooks/useAutoScroll";
import { useConversationHistory } from "../hooks/useConversationHistory";
import type { HistoryMessage, AIHistoryMessage } from "../types/HistoryMessage";

export function ChatBot() {
	const [query, setQuery] = useState("");
	const [modeId, setModeId] = useState<ModeId>("agent");
	const chatContainerRef = useRef<HTMLDivElement>(null);
	const contentEditableRef = useRef<HTMLDivElement>(null);

	const {
		uploadedFiles,
		handleFileChange,
		handleRemoveFile,
		clearUploadedFiles,
		hasUploadingFiles,
		getFileIds,
	} = useFileUpload();

	const [messages, setMessages] = useState<HistoryMessage[]>([]);
	const {
		setConversationId,
		setLoadConversation,
		setStartNewConversation,
		setHasMessages,
		conversationId,
	} = useChatContext();

	const { loading, error, submitChat, startNewConversation, stopStreaming, isThinking } =
		useChatStream({
			modeId,
			setConversationId,
			setMessages,
			messages,
			conversationId,
		});

	const { loadHistory, loading: loadingHistory } = useConversationHistory(
		conversationId,
		setMessages
	);

	useEffect(() => {
		if (error) {
			showToast(error, "error");
		}
	}, [error]);

	const handleStartNewConversation = useCallback(() => {
		clearUploadedFiles();
		setQuery("");
		startNewConversation();
	}, [startNewConversation, clearUploadedFiles]);

	const handleLoadConversation = useCallback(
		(id: string) => {
			clearUploadedFiles();
			setQuery("");
			setConversationId(id);
			loadHistory(id);
		},
		[setConversationId, clearUploadedFiles, loadHistory]
	);

	useEffect(() => {
		setStartNewConversation(() => handleStartNewConversation);
		setLoadConversation(() => handleLoadConversation);
	}, [
		handleStartNewConversation,
		handleLoadConversation,
		setStartNewConversation,
		setLoadConversation,
	]);

	useEffect(() => {
		setHasMessages(messages.length > 0);
	}, [messages.length, setHasMessages]);

	useAutoScroll({
		containerRef: chatContainerRef,
		messages,
		loading: loading || loadingHistory,
	});

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!query.trim() && modeId === "agent") return;
		if (hasUploadingFiles) {
			showToast("Veuillez attendre la fin du téléchargement des fichiers", "error");
			return;
		}

		const fileIds = getFileIds();
		try {
			setQuery("");
			clearUploadedFiles();
			await submitChat(query.trim(), fileIds);
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Échec de l'envoi de la demande";
			showToast(errorMessage, "error");
		}
	};

	const hasMessages = messages.length > 0;

	const chatProps = {
		query,
		modeId,
		modes: CHAT_MODES,
		uploadedFiles,
		loading: loading || loadingHistory,
		hasUploadingFiles,
		contentEditableRef,
		onQueryChange: setQuery,
		onModeIdChange: setModeId,
		onFileChange: handleFileChange,
		onRemoveFile: handleRemoveFile,
		onSubmit: handleSubmit,
		onStop: () => stopStreaming(),
	};

	if (!hasMessages && !loadingHistory) {
		return (
			<div className="p-2 h-full w-full max-w-xl mx-auto">
				<ChatInputForm {...chatProps} />
			</div>
		);
	}

	return (
		<div className="h-full flex flex-col w-full">
			<div ref={chatContainerRef} className="flex-1 shrink overflow-y-auto w-full">
				{loadingHistory && messages.length === 0 ? (
					<div className="flex flex-col items-center justify-center h-full gap-4">
						<span className="loading loading-spinner loading-lg text-primary"></span>
						<span className="text-sm text-base-content/60">Chargement de la conversation...</span>
					</div>
				) : (
					<div className="w-full max-w-xl mx-auto space-y-4 px-4 py-3 sm:px-6 sm:py-4">
						{messages.map((message, index) => {
							if (message.type === "human") {
								return (
									<UserMessage
										key={message.id || `user-${index}`}
										message={message as any}
										index={index}
									/>
								);
							}

							if (message.type === "ai") {
								const aiMsg = message as AIHistoryMessage;
								return (
									<div key={aiMsg.id || `assistant-${index}`} className="space-y-2">
										<AssistantMessage message={aiMsg as any} index={index} />
									</div>
								);
							}

							if (message.type === "tool") {
								const toolMsg = message as any;
								const status = toolMsg.status || 'loading';

								if (!toolMsg.tool_call_id) {
									return null;
								}

								// Try to get args from tool message, or from previous AI message's tool_calls
								let args = toolMsg.args;
								if (!args) {
									// Look for args in previous AI message's tool_calls
									for (let i = index - 1; i >= 0; i--) {
										const prevMsg = messages[i];
										if (prevMsg.type === "ai" && prevMsg.tool_calls) {
											const matchingToolCall = prevMsg.tool_calls.find(
												(tc) => tc.id === toolMsg.tool_call_id || tc.name === toolMsg.tool_name
											);
											if (matchingToolCall) {
												args = matchingToolCall.args;
												break;
											}
										}
									}
								}

								return (
									<ToolCall
										key={message.id || `tool-${index}`}
										id={toolMsg.run_id}
										name={toolMsg.tool_name || ''}
										status={status}
										args={args}
										result={toolMsg.content}
									/>
								);
							}

							return null;
						})}
						{isThinking && (
							<div className="prose prose-sm prose-neutral max-w-none">
								<span className="text-base-content/60 animate-pulse">Reflexion en cours...</span>
							</div>
						)}
					</div>
				)}
			</div>

			<div className="flex-none z-40 max-w-xl w-full mx-auto pb-2 px-2">
				<ChatInputForm {...chatProps} bottom />
			</div>
		</div>
	);
}
